import { useMemo, useState } from "react";
import { t } from "ttag";

import {
  useCreateCurrentPersonalAccessTokenMutation,
  useListCurrentPersonalAccessTokensQuery,
  useRevokeCurrentPersonalAccessTokenMutation,
} from "metabase/api";
import { CopyTextInput } from "metabase/common/components/CopyTextInput";
import { DelayedLoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper/DelayedLoadingAndErrorWrapper";
import { ClientSortableTable } from "metabase/common/components/Table";
import { useLocale } from "metabase/common/hooks/use-locale/use-locale";
import CS from "metabase/css/core/index.css";
import {
  Form,
  FormErrorMessage,
  FormProvider,
  FormSelect,
  FormSubmitButton,
  FormTextInput,
} from "metabase/forms";
import { formatDateTimeWithUnit } from "metabase/lib/formatting/date";
import { useSelector } from "metabase/lib/redux";
import { getApplicationName } from "metabase/selectors/whitelabel";
import {
  Button,
  Flex,
  Group,
  Icon,
  Modal,
  Stack,
  Text,
  Title,
} from "metabase/ui";
import { getThemeOverrides } from "metabase/ui/theme";
import { color } from "metabase/ui/utils/colors";
import type {
  CreatePersonalAccessTokenRequest,
  PersonalAccessToken,
} from "metabase-types/api";

const { fontFamilyMonospace } = getThemeOverrides();

type ModalState = null | "create" | "revoke" | "secret";

const formatMaybeDate = (value: string | null) =>
  value ? formatDateTimeWithUnit(value, "minute") : t`Never`;

const formatStatus = (token: PersonalAccessToken) => {
  switch (token.status) {
    case "active":
      return t`Active`;
    case "expired":
      return t`Expired`;
    case "revoked":
      return t`Revoked`;
  }
};

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <Stack
      mt="xl"
      align="center"
      justify="center"
      gap="sm"
      data-testid="empty-table-warning"
    >
      <Title order={2}>{t`No personal access tokens yet`}</Title>
      <Text mb="md" c="text-secondary">
        {t`Create an expiring token for scripts, automation, or API tools.`}
      </Text>
      <Button variant="filled" onClick={onCreate}>
        {t`Create token`}
      </Button>
    </Stack>
  );
}

function SecretTokenModal({
  secretKey,
  onClose,
}: {
  secretKey: string;
  onClose: () => void;
}) {
  return (
    <Modal
      size="30rem"
      opened
      onClose={onClose}
      title={t`Copy and save the token`}
      data-testid="secret-key-modal"
    >
      <Stack gap="xl" data-testid="secret-key-modal">
        <CopyTextInput
          label={t`The personal access token`}
          size="sm"
          value={secretKey}
          readOnly
          disabled
          styles={{
            input: {
              color: color("text-primary"),
              fontFamily: fontFamilyMonospace as string,
            },
          }}
        />
        <Flex direction="row" gap="md">
          <Icon
            name="info"
            size={22}
            className={CS.textMedium}
            style={{ marginTop: "-4px" }}
          />
          <Text size="sm" c="text-secondary">
            {t`Please copy this token and save it somewhere safe. For security reasons, we can't show it to you again.`}
          </Text>
        </Flex>
        <Group justify="flex-end">
          <Button onClick={onClose} variant="filled">
            {t`Done`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function RevokeTokenModal({
  token,
  onClose,
}: {
  token: PersonalAccessToken;
  onClose: () => void;
}) {
  const [revokeToken, { isLoading }] =
    useRevokeCurrentPersonalAccessTokenMutation();

  return (
    <Modal
      size="30rem"
      opened
      onClose={onClose}
      title={t`Revoke personal access token`}
    >
      <Stack gap="lg">
        <Text>{t`This token will stop working immediately.`}</Text>
        <Group justify="flex-end">
          <Button onClick={onClose}>{t`Cancel`}</Button>
          <Button
            variant="filled"
            color="error"
            loading={isLoading}
            onClick={async () => {
              await revokeToken(token.id).unwrap();
              onClose();
            }}
          >
            {t`Revoke token`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function CreateTokenModal({
  onClose,
  onShowSecret,
}: {
  onClose: () => void;
  onShowSecret: (secret: string) => void;
}) {
  const applicationName = useSelector(getApplicationName);
  const [createToken] = useCreateCurrentPersonalAccessTokenMutation();
  const expiryOptions = [
    { label: t`1 day`, value: "1" },
    { label: t`3 days`, value: "3" },
  ];

  return (
    <Modal
      size="30rem"
      opened
      onClose={onClose}
      title={t`Create personal access token`}
    >
      <FormProvider
        initialValues={{ name: "", expires_in_days: "1" }}
        onSubmit={async (values: { name: string; expires_in_days: string }) => {
          const response = await createToken({
            name: values.name,
            expires_in_days: Number(
              values.expires_in_days,
            ) as CreatePersonalAccessTokenRequest["expires_in_days"],
          }).unwrap();
          onShowSecret(response.unmasked_key);
        }}
      >
        <Form>
          <Stack gap="md">
            <FormTextInput
              name="name"
              label={t`Token name`}
              required
              maxLength={250}
            />
            <FormSelect
              name="expires_in_days"
              label={t`Expiration`}
              data={expiryOptions}
            />
            <Text c="text-secondary">
              {t`${applicationName} will use your existing permissions for this token and will show it only once.`}
            </Text>
            <FormErrorMessage />
            <Group justify="flex-end">
              <Button onClick={onClose}>{t`Cancel`}</Button>
              <FormSubmitButton variant="filled" label={t`Create token`} />
            </Group>
          </Stack>
        </Form>
      </FormProvider>
    </Modal>
  );
}

export const ManagePersonalAccessTokens = () => {
  const locale = useLocale();
  const [modal, setModal] = useState<ModalState>(null);
  const [activeToken, setActiveToken] = useState<PersonalAccessToken | null>(
    null,
  );
  const [secretKey, setSecretKey] = useState("");
  const columns = [
    { key: "name", name: t`Token name` },
    { key: "status", name: t`Status` },
    { key: "expires_at", name: t`Expires` },
    { key: "last_used_at", name: t`Last used` },
    { key: "actions", name: "", sortable: false },
  ];

  const { data, isLoading, error } = useListCurrentPersonalAccessTokensQuery();

  const rows = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }
    return [...data].sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  return (
    <Stack gap="lg">
      {modal === "create" && (
        <CreateTokenModal
          onClose={() => setModal(null)}
          onShowSecret={(secret) => {
            setSecretKey(secret);
            setModal("secret");
          }}
        />
      )}
      {modal === "secret" && (
        <SecretTokenModal
          secretKey={secretKey}
          onClose={() => {
            setSecretKey("");
            setModal(null);
          }}
        />
      )}
      {modal === "revoke" && activeToken && (
        <RevokeTokenModal
          token={activeToken}
          onClose={() => {
            setActiveToken(null);
            setModal(null);
          }}
        />
      )}
      <Group justify="space-between" align="flex-start">
        <Stack gap="xs">
          <Title order={2}>{t`Personal Access Tokens`}</Title>
          <Text c="text-secondary">
            {t`Create expiring tokens for programmatic access using your current permissions.`}
          </Text>
        </Stack>
        <Button variant="filled" onClick={() => setModal("create")}>
          {t`Create token`}
        </Button>
      </Group>
      {isLoading || error ? (
        <DelayedLoadingAndErrorWrapper loading={isLoading} error={error} />
      ) : rows.length === 0 ? (
        <EmptyState onCreate={() => setModal("create")} />
      ) : (
        <ClientSortableTable
          columns={columns}
          rows={rows}
          locale={locale}
          rowRenderer={(token: PersonalAccessToken) => (
            <tr>
              <td>{token.name}</td>
              <td>{formatStatus(token)}</td>
              <td>{formatMaybeDate(token.expires_at)}</td>
              <td>{formatMaybeDate(token.last_used_at)}</td>
              <td>
                {token.status === "active" && (
                  <Button
                    compact
                    variant="subtle"
                    onClick={() => {
                      setActiveToken(token);
                      setModal("revoke");
                    }}
                  >
                    {t`Revoke`}
                  </Button>
                )}
              </td>
            </tr>
          )}
        />
      )}
    </Stack>
  );
};
